/**
 * 计算方法注册表 — 独立于药物
 *
 * 新增计算方法只需:
 * 1. 在此添加 METHOD_CONFIG 条目
 * 2. 后端 pkpd_methods.py PKPD_REGISTRY 注册对应函数
 */

export interface MethodInput {
  name: string;
  label: string;
  min: number;
  max: number;
  step?: number;
  unit: string;
}

/** 单个结果列定义 */
export interface ResultColumn {
  key: string;       // 后端返回字段名 (在 pkpd_results[method] 下)
  label: string;     // 列标题
  unit: string;      // 单位
}

/** 候选方案表格列定义 */
export interface RegimenColumn {
  title: string;
  /** 数据来源: 'field' 直接取 regimen 字段, 'pkpd' 从 pkpd_results[method] 取值 */
  source: 'field' | 'pkpd';
  /** source='field' 时为 regimen 字段名, source='pkpd' 时为 pkpd_results 中的 key 路径 */
  dataKey: string;
  /** 格式化: number 保留小数位, percent 加 % */
  format?: 'number' | 'percent';
  decimals?: number;
  /** 候选方案表格的紧凑列宽（像素） */
  width?: number;
}

export interface MethodDef {
  label: string;
  /** 简短描述 (用于 tooltip) */
  description?: string;
  inputs: MethodInput[];
  /** 主指标 key (用于 primary_value 回退) */
  resultKey: string;
  resultLabel: string;
  resultUnit: string;
  /** 捆绑结果列: 当一个方法需要同时显示多列时使用 (如 %fT>MIC + PTA) */
  bundledResults?: ResultColumn[];
  /** 候选方案表格中的动态指标列 */
  regimenColumns: RegimenColumn[];
}

export const METHOD_CONFIG: Record<string, MethodDef> = {
  'Target AUC': {
    label: '目标AUC₂₄',
    description: '以AUC₂₄绝对值为靶标',
    inputs: [
      { name: 'target_auc', label: '目标AUC24', min: 200, max: 800, step: 50, unit: 'mg·h/L' },
    ],
    resultKey: 'auc24',
    resultLabel: '预测AUC₂₄',
    resultUnit: 'mg·h/L',
    regimenColumns: [
      { title: '预测AUC₂₄ (mg·h/L)', source: 'field', dataKey: 'predicted_auc24', format: 'number', decimals: 1, width: 72 },
      { title: '预测Cmin (mg/L)', source: 'field', dataKey: 'predicted_cmin', format: 'number', decimals: 2, width: 70 },
      { title: 'PTA (%)', source: 'field', dataKey: 'pta', format: 'percent', decimals: 1, width: 54 },
    ],
  },
  'AUC/MIC': {
    label: 'AUC₂₄/MIC',
    description: '以AUC₂₄与MIC比值为靶标',
    inputs: [
      { name: 'target_auc_mic', label: '目标AUC/MIC', min: 50, max: 1000, step: 50, unit: '' },
      { name: 'mic', label: 'MIC', min: 0.125, max: 64, step: 0.5, unit: 'mg/L' },
    ],
    resultKey: 'auc_mic_ratio',
    resultLabel: '预测AUC₂₄/MIC',
    resultUnit: '',
    regimenColumns: [
      { title: '目标AUC₂₄/MIC', source: 'field', dataKey: 'target_auc_mic', format: 'number', decimals: 1, width: 72 },
      { title: '预测AUC₂₄ (mg·h/L)', source: 'field', dataKey: 'predicted_auc24', format: 'number', decimals: 1, width: 72 },
      { title: 'PTA (%)', source: 'field', dataKey: 'pta', format: 'percent', decimals: 1, width: 54 },
    ],
  },
  '%fT>MIC': {
    label: '%fT>MIC',
    description: '游离药物浓度超过MIC的时间百分比',
    inputs: [
      { name: 'target_ft', label: '目标%fT>MIC', min: 20, max: 100, step: 5, unit: '%' },
      { name: 'mic', label: 'MIC', min: 0.125, max: 64, step: 0.5, unit: 'mg/L' },
    ],
    resultKey: 'ft_above_mic',
    resultLabel: '%fT>MIC',
    resultUnit: '%',
    bundledResults: [
      { key: 'value', label: '%fT>MIC', unit: '%' },
      { key: 'pta', label: 'PTA', unit: '%' },
    ],
    regimenColumns: [
      { title: '%fT>MIC (%)', source: 'pkpd', dataKey: 'value', format: 'percent', decimals: 1, width: 68 },
      { title: 'PTA (%)', source: 'pkpd', dataKey: 'pta', format: 'percent', decimals: 1, width: 54 },
    ],
  },
  'Target Concentration': {
    label: '目标Cₘᵢₙ',
    description: '以稳态谷浓度为靶标',
    inputs: [
      { name: 'target_cmin', label: '目标Cmin', min: 0.1, max: 50, step: 0.5, unit: 'mg/L' },
    ],
    resultKey: 'cmin',
    resultLabel: '预测Cmin',
    resultUnit: 'mg/L',
    regimenColumns: [
      { title: '预测Cmin (mg/L)', source: 'field', dataKey: 'predicted_cmin', format: 'number', decimals: 2, width: 70 },
      { title: '预测Cmax (mg/L)', source: 'field', dataKey: 'predicted_cmax', format: 'number', decimals: 2, width: 70 },
      { title: 'PTA (%)', source: 'field', dataKey: 'pta', format: 'percent', decimals: 1, width: 54 },
    ],
  },
  'Target Cmax': {
    label: '目标Cₘₐₓ',
    description: '以稳态峰浓度为靶标',
    inputs: [
      { name: 'target_cmax', label: '目标Cmax', min: 10, max: 60, step: 1, unit: 'mg/L' },
    ],
    resultKey: 'cmax',
    resultLabel: '预测Cmax',
    resultUnit: 'mg/L',
    regimenColumns: [
      { title: '预测Cmax (mg/L)', source: 'pkpd', dataKey: 'details.css_peak', format: 'number', decimals: 2, width: 70 },
      { title: '预测Cmin (mg/L)', source: 'pkpd', dataKey: 'details.css_trough', format: 'number', decimals: 2, width: 70 },
    ],
  },
  'Cmax/MIC': {
    label: 'Cₘₐₓ/MIC',
    description: '以峰浓度与MIC比值为靶标',
    inputs: [
      { name: 'target_cmax_mic', label: '目标Cmax/MIC', min: 1, max: 100, step: 1, unit: '' },
      { name: 'mic', label: 'MIC', min: 0.125, max: 64, step: 0.5, unit: 'mg/L' },
    ],
    resultKey: 'cmax_mic_ratio',
    resultLabel: 'Cmax/MIC',
    resultUnit: '',
    regimenColumns: [
      { title: 'Cmax/MIC', source: 'pkpd', dataKey: 'value', format: 'number', decimals: 1, width: 68 },
      { title: '预测Cmax (mg/L)', source: 'pkpd', dataKey: 'details.css_peak', format: 'number', decimals: 2, width: 70 },
    ],
  },
  'Remedial Dose': {
    label: '补服方案',
    description: '漏服或延迟服药后的候选补救方案搜索',
    inputs: [],
    resultKey: 'score',
    resultLabel: '综合评分',
    resultUnit: '',
    regimenColumns: [
      { title: '高于治疗窗 (h)', source: 'field', dataKey: 'time_above_upper_h', format: 'number', decimals: 2 },
      { title: '低于治疗窗 (h)', source: 'field', dataKey: 'time_below_lower_h', format: 'number', decimals: 2 },
      { title: '执行复杂度', source: 'field', dataKey: 'complexity_score', format: 'number', decimals: 1 },
      { title: '综合评分', source: 'field', dataKey: 'score', format: 'number', decimals: 2 },
    ],
  },
};
